import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { Container, Form, Button, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Compass, Plus, Search, Upload, X } from "lucide-react";
import { fetchGeoreferenceList } from "../../redux/slice/mapSlice";
import { useToast } from "../../context/TostContext";
import CustomTable from "../../components/table/CustomTable";
import StatusChip from "../../components/statusChip/StatusChip";
import DeletePopup from "../../components/delete-popup";
import { TableSearch } from "../../components/form/Fields";
import CustomSelect from "../../components/form/CustomSelect";
import Axios, { authHeaders, getProxiedImageUrl } from "../../utils/axios";
import { CheckboxOption, MultiValueMessage } from "../../components/form/utils";
import { io } from "socket.io-client";
import SharpenModal from "./SharpenModal";
import VectoriseModal from "../GisImport/VectoriseModal";

const customStyles = {
  menu: (provided) => ({
    ...provided,
    minWidth: "250px",
    zIndex: 9999
  }),
  multiValueRemove: () => ({
    display: 'none',
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: "12px",
    backgroundColor: state.isSelected
      ? "transparent"
      : state.isFocused
        ? "var(--primary-100)"
        : "transparent",
    color: 'var(-text-primary)',
    ':active': {
      backgroundColor: "var(--primary-100)"
    }
  })
};

export default function MapGeoreferenceModule() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const routerLocation = useLocation();
  const toast = useToast();

  // Local state for georeferenced map types
  const [georefMapTypes, setGeorefMapTypes] = useState([]);
  const georefList = useSelector((state) => state.maps.georefList);
  const isLoading = useSelector((state) => state.maps.isLoading);

  // Search & filter states initialized with sessionStorage for POP navigation
  const [limit, setLimit] = useState(() => {
    if (navigationType === "POP") {
      const savedLimit = sessionStorage.getItem("georef_limit");
      if (savedLimit) return JSON.parse(savedLimit);
    }
    return { value: 10, label: 10 };
  });

  const [searchQuery, setSearchQuery] = useState(() => {
    if (navigationType === "POP") {
      return sessionStorage.getItem("georef_searchQuery") || "";
    }
    return "";
  });

  const [selectedMapType, setSelectedMapType] = useState(() => {
    if (navigationType === "POP") {
      return sessionStorage.getItem("georef_selectedMapType") || "";
    }
    return "";
  });

  const [selectedLocation, setSelectedLocation] = useState(() => {
    if (navigationType === "POP") {
      const savedLoc = sessionStorage.getItem("georef_selectedLocation");
      if (savedLoc) return JSON.parse(savedLoc);
    }
    return [];
  });

  const [locationOptions, setLocationOptions] = useState([]);
  const [locationSearch, setLocationSearch] = useState("");

  const [selectedStatus, setSelectedStatus] = useState(() => {
    // The import report links straight to a tab (e.g. "to_check").
    if (routerLocation.state?.status) return routerLocation.state.status;
    if (navigationType === "POP") {
      return sessionStorage.getItem("georef_selectedStatus") || "all";
    }
    return "all";
  });

  const [currentPage, setCurrentPage] = useState(() => {
    if (navigationType === "POP") {
      const savedPage = sessionStorage.getItem("georef_page");
      if (savedPage) return parseInt(savedPage, 10);
    }
    return 1;
  });

  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

  // Save state to sessionStorage
  sessionStorage.setItem("georef_limit", JSON.stringify(limit));
  sessionStorage.setItem("georef_page", currentPage.toString());
  sessionStorage.setItem("georef_searchQuery", searchQuery);
  sessionStorage.setItem("georef_selectedMapType", selectedMapType);
  sessionStorage.setItem("georef_selectedLocation", JSON.stringify(selectedLocation));
  sessionStorage.setItem("georef_selectedStatus", selectedStatus);

  // Live Map Overlay Modal state
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedMapFile, setSelectedMapFile] = useState(null);
  const [opacity, setOpacity] = useState(0.75);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

  // Sharpen (re-render an old PDF sheet at full resolution) state
  const [sharpenRow, setSharpenRow] = useState(null);
  const [vectoriseRow, setVectoriseRow] = useState(null);
  const hideVectorise = useCallback(() => setVectoriseRow(null), []);

  // Delete Georeference state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalItem, setDeleteModalItem] = useState(null);

  const isInitialLoadRef = useRef(true);

  // Debounced search helper
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      if (!isInitialLoadRef.current) {
        setCurrentPage(1);
      } else {
        isInitialLoadRef.current = false;
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Load map types where is_map_georeference is true on mount
  useEffect(() => {
    const getGeorefMapTypes = async () => {
      try {
        const res = await Axios.get(
          "/maps/map-types/all?is_active=true&is_map_georeference=true",
          authHeaders()
        );
        setGeorefMapTypes(res.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch georeference map types", err);
      }
    };
    getGeorefMapTypes();
  }, []);

  const mergedLocationOptions = useMemo(() => {
    return [
      ...(selectedLocation || []),
      ...locationOptions.filter(
        (opt) => !(selectedLocation || []).some((sel) => sel.value === opt.value)
      ),
    ];
  }, [selectedLocation, locationOptions]);

  // Load locations dynamically
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await Axios.get("/maps/map-locations/all", {
          params: {
            map_type_id: selectedMapType || "",
            search: locationSearch,
            page: 1,
            limit: 50,
          },
          ...authHeaders(),
        });
        setLocationOptions(
          (res.data?.data || []).map((l) => ({
            value: l.id,
            label: l.location_path || l.name,
          }))
        );
      } catch (err) {
        console.error("Failed to load locations", err);
      }
    };

    const handler = setTimeout(() => {
      fetchLocations();
    }, 300);

    return () => clearTimeout(handler);
  }, [selectedMapType, locationSearch]);

  // The list request for the current filters, tab and page.
  const listParams = useMemo(
    () => ({
      page: currentPage,
      limit: limit.value,
      search: debouncedSearch,
      map_type_id: selectedMapType,
      is_georeferenced: selectedStatus === "completed" ? "true" : selectedStatus === "pending" ? "false" : "",
      to_check: selectedStatus === "to_check" ? "true" : "",
      location_ids: (selectedLocation || []).map((l) => l.value).join(","),
    }),
    [currentPage, limit.value, debouncedSearch, selectedMapType, selectedStatus, selectedLocation]
  );
  const reload = useCallback(() => dispatch(fetchGeoreferenceList(listParams)), [dispatch, listParams]);
  // For the socket handlers, which shouldn't reconnect every time a filter changes.
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  // Fetch map files list when filters or pages change
  useEffect(() => {
    reload();
  }, [reload]);

  // Socket.IO real-time notification listener for tile generation progress
  useEffect(() => {
    let socket;
    try {
      let token = localStorage.getItem("auth-token");
      if (!token && localStorage.getItem("userData")) {
        try {
          token = JSON.parse(localStorage.getItem("userData"))?.token;
        } catch (e) {}
      }
      if (!token) token = localStorage.getItem("token") || sessionStorage.getItem("token") || "";

      let socketUrl = process.env.REACT_APP_SOCKET_URL;
      if (!socketUrl && process.env.REACT_APP_BASE_URL) {
        try {
          const parsed = new URL(process.env.REACT_APP_BASE_URL);
          socketUrl = `${parsed.protocol}//${parsed.host}`;
        } catch (e) {}
      }
      if (!socketUrl) socketUrl = process.env.REACT_APP_API_BASE_URL || "http://192.168.0.190:4003";

      console.log("[Socket Init] Connecting to socket server at:", socketUrl, "Token available:", !!token);

      socket = io(socketUrl, {
        transports: ["websocket", "polling"],
        auth: { token },
        query: { token }
      });

      socket.on("connect", () => {
        console.log("[Socket] Connected successfully with ID:", socket.id);
        socket.emit("join_room", "admin");
        socket.emit("join", "admin");
      });

      socket.on("connect_error", (err) => {
        console.error("[Socket Error] Connection failed:", err.message);
      });

      socket.on("disconnect", (reason) => {
        console.warn("[Socket Warning] Disconnected:", reason);
      });

      socket.on("georeference_tiling_status", (data) => {
        console.log("[Socket Event] georeference_tiling_status received:", data);
        if (data?.status === "completed") {
          toast.success(data.message || "Map tile generation completed successfully!");
          reloadRef.current();
        } else if (data?.status === "failed") {
          toast.error(data.message || "Map tile generation failed.");
        }
      });

      // Sharpening: phase 1 done (now tiling, so the row shows Processing), or failed.
      socket.on("georeference_sharpen_status", (data) => {
        if (data?.status === "failed") {
          toast.error(data.message || "Sharpening failed; the sheet is unchanged.");
          return;
        }
        reloadRef.current();
      });
    } catch (err) {
      console.error("[Socket Init Error]:", err);
    }

    return () => {
      if (socket) socket.disconnect();
    };
  }, [toast]);

  // Modal Location Search State
  const [modalSearchQuery, setModalSearchQuery] = useState("");
  const [isModalSearching, setIsModalSearching] = useState(false);

  const handleModalLocationSearch = async (e) => {
    if (e) e.preventDefault();
    const query = modalSearchQuery.trim();
    if (!query || !mapRef.current) return;

    const coordMatch = query.match(/^(-?\d+(?:\.\d+)?)\s*[\s,]\s*(-?\d+(?:\.\d+)?)$/);
    if (coordMatch) {
      const lat = parseFloat(coordMatch[1]);
      const lng = parseFloat(coordMatch[2]);
      if (!isNaN(lat) && !isNaN(lng)) {
        mapRef.current.flyTo({ center: [lng, lat], zoom: 15 });
        return;
      }
    }

    try {
      setIsModalSearching(true);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`
      );
      const results = await response.json();
      if (results && results.length > 0) {
        const place = results[0];
        const lng = parseFloat(place.lon);
        const lat = parseFloat(place.lat);
        mapRef.current.flyTo({ center: [lng, lat], zoom: 14 });
      } else {
        toast.error("No location found for: " + query);
      }
    } catch (err) {
      console.error("Modal location search failed:", err);
    } finally {
      setIsModalSearching(false);
    }
  };

  // Handle live map modal rendering
  useEffect(() => {
    if (!showMapModal || !selectedMapFile || !mapContainerRef.current) return;

    const { warped_file_url, tiled_file_url, corners } = selectedMapFile;

    const mapOptions = {
      container: mapContainerRef.current,
      style: "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json",
      center: [72.5099, 23.04],
      zoom: 12,
      dragRotate: false,
    };

    let calculatedBounds = null;
    let flatBounds = null;

    if (corners && corners.length >= 4) {
      const lngs = corners.map((c) => c[0]);
      const lats = corners.map((c) => c[1]);
      flatBounds = [
        Math.min(...lngs),
        Math.min(...lats),
        Math.max(...lngs),
        Math.max(...lats),
      ];
      calculatedBounds = [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ];
    }

    console.log("Initializing Overlay MapLibre Instance for inspection...", selectedMapFile);
    const map = new maplibregl.Map(mapOptions);

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");

    map.on("load", () => {
      // Add satellite base tile source
      map.addSource("esri-sat-source", {
        type: "raster",
        tiles: [
          "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        ],
        tileSize: 256,
        maxzoom: 19,
      });

      map.addLayer({
        id: "esri-sat-layer",
        type: "raster",
        source: "esri-sat-source",
        layout: { visibility: "visible" },
      });

      if (warped_file_url && corners && corners.length >= 4) {
        if (tiled_file_url) {
          const cleanTiledFileUrl = tiled_file_url.split("?")[0];
          const baseUrl = cleanTiledFileUrl.split("/{z}")[0];
          const extension = cleanTiledFileUrl.substring(cleanTiledFileUrl.lastIndexOf("."));
          const proxiedTileUrl = `${getProxiedImageUrl(baseUrl)}/{z}/{x}/{y}${extension}`;

          map.addSource("warped-preview-source", {
            type: "raster",
            tiles: [proxiedTileUrl],
            tileSize: 256,
            bounds: flatBounds,
            minzoom: 0,
            maxzoom: 22,
          });
        } else {
          map.addSource("warped-preview-source", {
            type: "image",
            url: getProxiedImageUrl(warped_file_url),
            coordinates: corners,
          });
        }

        map.addLayer({
          id: "warped-preview-layer",
          type: "raster",
          source: "warped-preview-source",
          paint: {
            "raster-opacity": Number(opacity),
          },
        });

        if (calculatedBounds) {
          map.fitBounds(calculatedBounds, { padding: 60, animate: false });
        }
        map.resize();
      }
    });

    // Delayed resize to ensure Bootstrap Modal layout has finished animating
    const t1 = setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.resize();
        if (calculatedBounds) {
          mapRef.current.fitBounds(calculatedBounds, { padding: 60, animate: false });
        }
      }
    }, 200);

    const t2 = setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.resize();
        if (calculatedBounds) {
          mapRef.current.fitBounds(calculatedBounds, { padding: 60, animate: false });
        }
      }
    }, 500);

    mapRef.current = map;
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMapModal, selectedMapFile]);

  // Update opacity dynamically in Live Map Overlay Inspector
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    try {
      if (map.getLayer("warped-preview-layer")) {
        map.setPaintProperty("warped-preview-layer", "raster-opacity", Number(opacity));
      }
    } catch (err) {
      console.warn("Error setting opacity on warped preview layer:", err);
    }
  }, [opacity]);

  const handleDeleteGeoreference = async () => {
    if (!deleteModalItem) return;
    try {
      await Axios.delete(`/maps/map-files/${deleteModalItem.id}/georeference`, authHeaders());
      toast.success("Georeferenced map reset successfully!");
      reload();
    } catch (err) {
      console.error("Failed to delete georeference:", err);
      toast.error(err?.response?.data?.message || "Failed to reset georeference.");
    } finally {
      setShowDeleteModal(false);
      setDeleteModalItem(null);
    }
  };

  const columns = useMemo(() => [
    {
      id: "sr_no",
      header: "SR NO.",
      cell: (_, row, index) => (currentPage - 1) * limit.value + index + 1,
    },
    {
      id: "file_name",
      header: "FILE NAME",
      cell: (_, row) => row.file_name || "-",
    },
    {
      id: "title",
      header: "NAME",
      cell: (_, row) => row.title || "-",
    },
    {
      id: "map_type",
      header: "MAP TYPE",
      cell: (_, row) => row.mapType?.code?.toUpperCase() || "-",
    },
    {
      id: "location",
      header: "LOCATION",
      cell: (_, row) => row.location_path || row.location?.name || "Global / Root",
    },
    {
      id: "status",
      header: "GEOREF STATUS",
      cell: (_, row) => {
        if (!row.georeference) {
          // Imported but not on the map yet: waiting for an admin.
          if (row.georefDraft?.mode === "image_only") {
            return <StatusChip bg="secondary" pill title="To check: place by hand" />;
          }
          if (row.georefDraft) {
            return <StatusChip bg="primary" pill title="To check" />;
          }
          return <StatusChip bg="warning" pill title="Pending" />;
        }
        // Sharpening runs for minutes before tiling starts, while the sheet
        // itself still reads as completed; the server reports that stage here.
        if (row.sharpen?.stage === "checking") {
          return <StatusChip bg="info" pill title="Checking sharpness..." />;
        }
        if (row.sharpen?.stage === "rendering") {
          return <StatusChip bg="info" pill title="Sharpening: rendering..." />;
        }
        if (row.sharpen?.stage === "failed") {
          return (
            <span title={row.sharpen.message || ""}>
              <StatusChip bg="danger" pill title="Sharpen failed" />
            </span>
          );
        }
        const tilingStatus = row.georeference.tiling_status;
        if (tilingStatus === "processing") {
          return <StatusChip bg="info" pill title="Processing Tiling..." />;
        }
        if (tilingStatus === "failed") {
          return <StatusChip bg="danger" pill title="Tiling Failed" />;
        }
        return (
          <span className="d-inline-flex align-items-center gap-1">
            <StatusChip bg="success" pill title="Georeferenced" />
            {row.georeference.source === "import" && (
              <StatusChip bg="light" pill title="Imported" className="border text-dark" />
            )}
          </span>
        );
      },
    },
    // What the import knew about a sheet waiting to be checked.
    ...(selectedStatus === "to_check"
      ? [
          {
            id: "confidence",
            header: "CONFIDENCE",
            cell: (_, row) => {
              const meta = row.georefDraft?.import_meta || {};
              if (!meta.confidence) return "-";
              return (
                <span className="text-nowrap">
                  {meta.confidence}
                  {typeof meta.fit_score === "number" && <span className="text-muted"> · fit {meta.fit_score.toFixed(2)}</span>}
                </span>
              );
            },
          },
          {
            id: "review_reasons",
            header: "WHY",
            cell: (_, row) => {
              const reasons = row.georefDraft?.import_meta?.review_reasons || [];
              if (!reasons.length) return "-";
              return (
                <div className="small" style={{ minWidth: 260, maxWidth: 380, whiteSpace: "normal" }}>
                  {reasons.map((r) => (
                    <div key={r}>{r}</div>
                  ))}
                </div>
              );
            },
          },
        ]
      : []),
  ], [currentPage, limit.value, selectedStatus]);

  const actionList = useMemo(() => [
    {
      title: "Georeference",
      handler: (row) => {
        const fileUrl = row.file_url || "";
        const isImage = fileUrl.match(/\.(jpg|jpeg|png|webp|pdf)$/i);
        if (isImage) {
          navigate(`/maps/${row.mapType?.code?.toLowerCase()}/georeference/${row.id}`);
        } else {
          toast.error("Georeferencing is only supported for image formats (PNG, JPG, JPEG) and PDF.");
        }
      },
    },
    {
      title: "View Overlay",
      handler: (row) => {
        if (!row.georeference || !row.georeference.warped_file_url) {
          toast.error("Please georeference the sheet first before inspecting overlays.");
          return;
        }
        setSelectedMapFile(row.georeference);
        setShowMapModal(true);
      },
    },
    {
      // Zoning and survey parcels from the PDF's own drawing, as a draft to check.
      title: "Vectorise",
      isVisible: (row) => !!row.georeference && /\.pdf$/i.test(row.file_url || ""),
      handler: (row) => setVectoriseRow(row),
    },
    {
      // Old PDF sheets were rendered at a low resolution (and, if cropped, from
      // a low-resolution cut-out). Not shown once a sheet has been rendered
      // the new way: render_dpi is set then.
      title: "Sharpen PDF",
      isVisible: (row) =>
        row.georeference?.tiling_status === "completed" &&
        (!row.sharpen || row.sharpen.stage === "failed") &&
        !row.georeference?.render_dpi &&
        /\.pdf$/i.test(row.file_url || ""),
      handler: (row) => setSharpenRow(row),
    },
    {
      title: "Retry Tiling",
      isVisible: (row) => row.georeference?.tiling_status === "failed",
      handler: async (row) => {
        try {
          await Axios.post(`/maps/map-files/${row.id}/georeference/retry-tiling`, {}, authHeaders());
          toast.success("Tile generation re-queued in background!");
          reload();
        } catch (err) {
          toast.error("Failed to retry tile generation.");
        }
      },
    },
    {
      title: "Delete Georeference",
      isVisible: (row) => !!row.georeference,
      handler: (row) => {
        if (!row.georeference) {
          toast.error("Map is not georeferenced yet.");
          return;
        }
        setDeleteModalItem(row);
        setShowDeleteModal(true);
      },
    },
  ].filter(Boolean), [navigate, toast, reload]);

  const paginationConfig = useMemo(() => ({
    currentPage,
    totalPages: georefList?.pagination?.total_pages || 1,
    total: georefList?.pagination?.total || 0,
    onPageChange: (page) => setCurrentPage(page),
    pageSize: limit.value,
    setPageSize: (size) => {
      setLimit({ value: size, label: size });
      setCurrentPage(1);
    },
    setCurrentPage,
  }), [currentPage, georefList, limit]);

  const headerPortalTarget = document.getElementById("header-portal-target");

  const headerContent = (
    <div className="d-flex align-items-center justify-content-between w-100 pe-3">
      <h4 className="fw-semibold mb-0 text-primary fs-lg">
        Map Georeferences <span className="text-muted fw-normal">/ Standalone Workspace</span>
      </h4>
    </div>
  );

  const mapTypeOptions = useMemo(() => {
    return [
      { value: "", label: "All Map Types" },
      ...(georefMapTypes || []).map((type) => ({
        value: String(type.id),
        label: `${type.name} (${type.code?.toUpperCase()})`,
      })),
    ];
  }, [georefMapTypes]);

  return (
    <>
      {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}

      <Container fluid className="px-0">
        <div className="bg-white rounded-2 overflow-hidden common-shadow mt-4">
          {/* Header Filter Section */}
          <div className="p-4 d-flex align-items-center justify-content-between flex-wrap gap-3">
            {/* Left side: Search, Dropdown, Status Tabs */}
            <div className="d-flex align-items-center flex-wrap gap-3">
              {/* Search query (First) */}
              <div style={{ minWidth: "260px" }}>
                <TableSearch
                  placeholder="Search files by title/filename..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClear={() => setSearchQuery("")}
                />
              </div>

              {/* Map Type Filter (Second) */}
              <div style={{ minWidth: "200px" }}>
                <CustomSelect
                  placeholder="All Map Types"
                  options={mapTypeOptions}
                  value={mapTypeOptions.find((opt) => String(opt.value) === String(selectedMapType)) || mapTypeOptions[0]}
                  onChange={(option) => {
                    setSelectedMapType(option?.value || "");
                    setSelectedLocation([]);
                    setCurrentPage(1);
                  }}
                  isClearable={false}
                  label={{ isHidden: true }}
                  className="mb-0"
                />
              </div>

              {/* Location Filter */}
              <div style={{ minWidth: "220px" }}>
                <CustomSelect
                  placeholder="All"
                  options={mergedLocationOptions}
                  value={selectedLocation}
                  inputValue={locationSearch}
                  isMulti={true}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  selectProps={{
                    components: { Option: CheckboxOption, MultiValue: MultiValueMessage },
                    styles: customStyles
                  }}
                  onInputChange={(val, { action }) => {
                    if (action === "input-change") {
                      setLocationSearch(val);
                    } else if (action === "menu-close") {
                      setLocationSearch("");
                    }
                  }}
                  onChange={(option) => {
                    setSelectedLocation(option || []);
                    setCurrentPage(1);
                  }}
                  isClearable={true}
                  label={{ isHidden: true }}
                  className="mb-0"
                />
              </div>

              {/* Status Tabs (Third) */}
              <div className="d-flex align-items-center gap-2">
                <Button
                  variant={selectedStatus === "all" ? "primary" : "outline-secondary"}
                  size="sm"
                  style={{ height: "38px" }}
                  className="d-flex align-items-center"
                  onClick={() => { setSelectedStatus("all"); setCurrentPage(1); }}
                >
                  All Sheets
                </Button>
                <Button
                  variant={selectedStatus === "pending" ? "primary" : "outline-secondary"}
                  size="sm"
                  style={{ height: "38px" }}
                  className="d-flex align-items-center"
                  onClick={() => { setSelectedStatus("pending"); setCurrentPage(1); }}
                >
                  Pending Georef
                </Button>
                <Button
                  variant={selectedStatus === "completed" ? "primary" : "outline-secondary"}
                  size="sm"
                  style={{ height: "38px" }}
                  className="d-flex align-items-center"
                  onClick={() => { setSelectedStatus("completed"); setCurrentPage(1); }}
                >
                  Completed Georef
                </Button>
                <Button
                  variant={selectedStatus === "to_check" ? "primary" : "outline-secondary"}
                  size="sm"
                  style={{ height: "38px" }}
                  className="d-flex align-items-center gap-1"
                  title="Imported sheets that aren't on the map until someone checks and saves them"
                  onClick={() => { setSelectedStatus("to_check"); setCurrentPage(1); }}
                >
                  To check
                  {georefList?.to_check_total > 0 && (
                    <span className={`badge rounded-pill ${selectedStatus === "to_check" ? "bg-white text-primary" : "bg-primary"}`}>
                      {georefList.to_check_total}
                    </span>
                  )}
                </Button>
              </div>
            </div>

            {/* Right side: Import package + Add Georeference */}
            <div className="d-flex align-items-center gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                className="d-flex align-items-center gap-1.5"
                onClick={() => navigate("/maps-georeference/import")}
              >
                <Upload size={16} />
                Import package
              </Button>
              <Button
                variant="primary"
                size="sm"
                className="d-flex align-items-center gap-1.5"
                onClick={() => navigate("/maps-georeference/create")}
              >
                <Plus size={16} />
                Add Georeference
              </Button>
            </div>
          </div>

          {/* Main Data Table */}
          <CustomTable
            data={georefList?.data || []}
            columns={columns}
            loading={isLoading}
            emptyMessage={
              selectedStatus === "to_check"
                ? "Nothing to check. Imported drafts appear here until they are saved in the workspace."
                : "No map sheets found for selected filters."
            }
            keyField="id"
            actionList={actionList}
            pagination={paginationConfig}
          />
        </div>
      </Container>

      {/* Live Map Overlay Inspector Modal */}
      <Modal
        show={showMapModal}
        onHide={() => {
          setShowMapModal(false);
          setSelectedMapFile(null);
        }}
        size="lg"
        centered
        keyboard={false}
        className="overlay-modal"
      >
        <Modal.Header closeButton className="bg-light border-0 py-2.5 d-flex align-items-center justify-content-between">
          <Modal.Title className="fs-6 fw-semibold text-primary me-3 text-truncate" style={{ maxWidth: "340px" }}>
            <Compass size={18} className="me-1.5 align-middle text-indigo-500" />
            Live Inspector: <span className="text-muted fw-normal">{selectedMapFile?.title || selectedMapFile?.file_name}</span>
          </Modal.Title>
          {/* Location Search Bar */}
          <Form onSubmit={handleModalLocationSearch} className="d-flex align-items-center me-3">
            <div className="position-relative d-flex align-items-center">
              <Form.Control
                type="text"
                size="sm"
                placeholder="Find place or lat, lng..."
                value={modalSearchQuery}
                onChange={(e) => setModalSearchQuery(e.target.value)}
                className="pe-4 fs-xs"
                style={{ width: "190px", height: "30px", fontSize: "11px" }}
              />
              {modalSearchQuery ? (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={() => setModalSearchQuery("")}
                  className="position-absolute end-0 p-1 text-secondary border-0"
                >
                  <X size={13} />
                </Button>
              ) : (
                <Button
                  type="submit"
                  variant="link"
                  size="sm"
                  disabled={isModalSearching}
                  className="position-absolute end-0 p-1 text-secondary border-0"
                >
                  {isModalSearching ? (
                    <div className="spinner-border spinner-border-sm" role="status" style={{ width: "12px", height: "12px" }} />
                  ) : (
                    <Search size={13} />
                  )}
                </Button>
              )}
            </div>
          </Form>
        </Modal.Header>
        <Modal.Body className="p-0 position-relative" style={{ height: "450px" }}>
          <div ref={mapContainerRef} className="w-100 h-100 position-absolute" />

          {/* Floating Opacity Control Widget */}
          <div
            className="position-absolute bg-white p-2.5 rounded shadow-sm d-flex align-items-center gap-2"
            style={{
              bottom: "16px",
              left: "16px",
              zIndex: 1000,
              border: "1px solid #e2e8f0",
              minWidth: "220px",
            }}
          >
            <span className="fw-semibold fs-xs text-secondary" style={{ fontSize: "12px" }}>Opacity:</span>
            <Form.Range
              min={0.1}
              max={1.0}
              step={0.05}
              value={opacity}
              onChange={(e) => setOpacity(parseFloat(e.target.value))}
              className="flex-grow-1"
              style={{ cursor: "pointer" }}
            />
            <span className="badge bg-secondary fs-xs" style={{ fontSize: "11px" }}>{Math.round(opacity * 100)}%</span>
          </div>
        </Modal.Body>
      </Modal>

      <VectoriseModal row={vectoriseRow} onHide={hideVectorise} />

      <SharpenModal
        row={sharpenRow}
        onHide={() => setSharpenRow(null)}
        onApplied={reload}
      />

      {/* Delete Georeference Confirmation Modal */}
      <DeletePopup
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setDeleteModalItem(null);
        }}
        title="Reset Georeference?"
        content="Are you sure you want to delete/reset the georeference for this map sheet? This will revert its status back to Pending and remove the warped overlay."
        saveTitle="Reset Map"
        onConfirm={handleDeleteGeoreference}
      />
    </>
  );
}
